import { stringify } from 'querystring';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { useHeaderText } from '@/features/Feed/hooks/useHeaderText';
import { useQueryParams } from '@/hooks/useQueryParams';
import { SEARCH_ROUTES } from '@/nav-routes';
import { useNavigate } from '@/router';

import { useCurrentFeedOption } from './useCurrentFeedOption';

const LANDING_PAGE = import.meta.env.VITE_DEFAULT_LANDING_PAGE || '';

export const useSearchImplementation = () => {
  const navigate = useNavigate();

  const navigateRef = React.useRef(navigate);

  const [, queryParams] = useQueryParams();
  const { pathname } = useLocation();
  const { showCurrentFeedOption, routeMatch } = useCurrentFeedOption();
  const { location: dropdownTagName } = useHeaderText();

  const subCategory = routeMatch?.params?.['subCategory'] ?? '';
  const activeCategoryTab = subCategory === 'all' ? '' : `/${subCategory}`;

  const onMyDataSearch = React.useCallback(
    (searchValue: string) => {
      const newParams: Record<string, string> = {};
      if (searchValue.length) {
        newParams['search'] = searchValue.trim();
        if (pathname === SEARCH_ROUTES.FULLSCREEN) {
          newParams['search_page'] = 'true';
        }
      } else {
        delete newParams['search'];
      }

      navigateRef.current.push(
        {
          pathname: '/search' + LANDING_PAGE,
          search: stringify(newParams),
        },
        null,
        { removeOldQueryParams: true }
      );
    },
    [pathname]
  );

  const onCurrentFeedSearch = React.useCallback(
    (searchValue: string) => {
      const newParams = { ...queryParams };

      if (searchValue.length) {
        newParams['search'] = searchValue.trim();
      } else {
        delete newParams['search'];
      }

      // '/feeds/board_id' or '/channels/channel_id'
      const feedBaseUrl = pathname.split('/').slice(0, 3).join('/');

      navigateRef.current.push(
        {
          pathname: `${feedBaseUrl}/today`,
          search: stringify(newParams),
        },
        null,
        { removeOldQueryParams: true }
      );
    },
    [pathname, queryParams]
  );

  const onNeedlWebSearch = React.useCallback(
    (searchValue: string) => {
      const newParams: Record<string, string> = {};
      if (searchValue.length) {
        newParams['search_type'] = SEARCH_TYPE.NEEDL_WEB;
        newParams['search'] = searchValue.trim();
        if (pathname === SEARCH_ROUTES.FULLSCREEN) {
          newParams['search_page'] = 'true';
        }
      } else {
        delete newParams['search'];
      }

      navigateRef.current.push(
        {
          pathname: '/search' + LANDING_PAGE,
          search: stringify(newParams),
        },
        null,
        { removeOldQueryParams: true }
      );
    },
    [pathname]
  );

  const onSharedDataSearch = React.useCallback(
    (searchValue: string) => {
      const newParams: Record<string, string> = {};
      if (searchValue.length) {
        newParams['search_type'] = SEARCH_TYPE.SHARED_DATA;
        newParams['search'] = searchValue.trim();
        if (pathname === SEARCH_ROUTES.FULLSCREEN) {
          newParams['search_page'] = 'true';
        }
      } else {
        delete newParams['search'];
      }

      navigateRef.current.push(
        {
          pathname: '/search/today',
          search: stringify(newParams),
        },
        null,
        { removeOldQueryParams: true }
      );
    },
    [pathname]
  );

  React.useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  return React.useMemo(
    () => ({
      showCurrentFeedOption,
      dropdownTagName,
      onMyDataSearch,
      onCurrentFeedSearch,
      onNeedlWebSearch,
      onSharedDataSearch,
      activeCategoryTab,
    }),
    [
      activeCategoryTab,
      dropdownTagName,
      onCurrentFeedSearch,
      onMyDataSearch,
      onNeedlWebSearch,
      onSharedDataSearch,
      showCurrentFeedOption,
    ]
  );
};

export const SEARCH_TYPE = {
  NEEDL_WEB: 'needl_web',
  SHARED_DATA: 'shared_data',
} as const;
